import type { CompetitorRecord } from "@/lib/compare/types";

const PRICING = "https://quso.ai/pricing";
const FAQ = "https://quso.ai/help/general-faq";
const CHECKED = "2026-10-08";

export const QUSO: CompetitorRecord = {
  slug: "quso",
  name: "Quso.ai",
  aliases: ["Vidyo.ai", "vidyo ai", "quso", "quso ai", "Quso AI"],
  url: "https://quso.ai",
  pricingUrl: PRICING,
  category: "clipper",
  positioning: "1 long video, 10 viral clips. Done in one click.",
  plans: [
    {
      name: "Free",
      monthly: 0,
      metered: "75 credits per month, 1 credit per processed minute",
      includes: [
        "720p render quality",
        "Watermark on exports",
        "7 days data retention",
        "TikTok publishing",
        "Uploads up to 5 GB at 720p",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "The watermark is stated on Quso.ai's own Opus Clip comparison page, not the pricing page.",
    },
    {
      name: "Lite",
      monthly: 29,
      annualPerMonth: 19,
      metered: "100 credits per month, or 200 per month on annual billing",
      includes: [
        "Unlimited 1080p clips",
        "Advanced editor",
        "16:9, 9:16 and 1:1 aspects",
        "10 GB storage",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Annual billing doubles the monthly credit allowance.",
    },
    {
      name: "Essential",
      monthly: 39,
      annualPerMonth: 26,
      metered: "300 credits per month, or 600 per month on annual billing",
      includes: [
        "Schedule to 7 platforms",
        "Content Planner",
        "Filler and silence removal",
        "25 GB storage",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Growth",
      monthly: 49,
      annualPerMonth: 33,
      metered: "600 credits per month, or 1,200 per month on annual billing",
      includes: [
        "Custom brand kit and templates",
        "Social analytics",
        "Viddy AI",
        "75 GB storage",
        "Built for teams",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
  ],
  entryPlanName: "Lite",
  entryPlanIncludes:
    "100 credits a month, which is 100 minutes of processed video, or 200 a month on annual billing, with 1080p clips and 10 GB storage",
  freeTier: "75 credits a month at 720p with a watermark and 7-day data retention",
  trial: "None stated; the free plan stands in for a trial",
  meteringUnit: "1 credit per minute of processed video, rounded up to the next minute",
  exportResolution: "720p on Free, 1080p on Lite and up",
  features: {
    longToShort: { v: "yes", note: "10 clips from one long video" },
    momentRanking: { v: "yes", note: "Virality score per clip" },
    reframing: { v: "yes", note: "Auto reframe plus CutMagic scene detection" },
    maxSource: { v: "text", note: "Size caps only: 5 GB free, 15 GB paid" },
    layouts: { v: "text", note: "Aspects 16:9, 9:16, 1:1; layouts not enumerated" },
    redditStory: { v: "no" },
    fakeText: { v: "no" },
    aiImages: { v: "no", note: "No image generator advertised" },
    voiceover: { v: "partial", note: "Text to speech, listed on their Opus page only" },
    voiceCloning: { v: "n/a", note: "Not advertised" },
    avatars: { v: "n/a", note: "Not advertised" },
    timelineEditor: { v: "partial", note: "Doc-style editing; advanced editor on Lite and up" },
    captionStyles: { v: "n/a", note: "Count not published" },
    captionLanguages: { v: "text", note: "50+ or 100+; homepage says both" },
    bRoll: { v: "yes", note: "AI B-Roll Library" },
    fourK: { v: "no", note: "1080p maximum on paid plans" },
    scheduling: { v: "yes", note: "7 platforms on Essential and up" },
    analytics: { v: "partial", note: "Growth plan only" },
    socialTracker: { v: "no" },
    api: { v: "no", note: "FAQ answers: Not at the moment" },
    webhooks: { v: "n/a", note: "Not advertised; no API" },
    web: { v: "yes" },
    desktop: { v: "no", note: "Browser app" },
    mobile: { v: "n/a", note: "No app advertised" },
    teamSeats: { v: "partial", note: "Shared credits in workspaces; no seat pricing" },
    freeTier: { v: "yes", note: "75 credits, 720p, watermark, 7-day retention" },
    trial: { v: "n/a", note: "Not stated; free plan instead" },
    metering: { v: "text", note: "1 credit per processed minute, rounded up" },
    rollover: { v: "no", note: "Monthly credits expire; add-on credits never expire" },
    refunds: { v: "text", note: "Non-refundable on monthly and annual" },
    cancelInApp: { v: "n/a", note: "Not described in the FAQ" },
  },
  billingTerms: {
    refunds:
      "The general FAQ states Monthly: Non-refundable and Annual: Non-refundable. Failed videos are auto-refunded in credits.",
    cancel: "Cancellation steps are not described in the general FAQ or on the pricing page.",
    sourceUrl: FAQ,
    checkedAt: CHECKED,
  },
  quotes: [],
  verification: {
    method: "fetch",
    date: CHECKED,
    note: "Pricing page and general FAQ fetched directly on Oct 8, 2026. Monthly prices and the per-month billed-yearly labels ($19, $26, $33) come from the pricing page; the page prints no annual totals, so none are recorded. The Free plan watermark comes from quso.ai/alternatives/opus-clip-alternative. The homepage states both 50+ and 100+ caption languages; both are kept. No figure is third-party.",
  },
  seoPattern: "quso.ai/alternatives/<competitor>-alternative, 40 pages, no page about Crayo",
};
